// resourceLoader.js

// Utility to determine the file extension from a filename or URL
const getFileType = (filename) => {
    if (!filename) return null;
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex === -1 ? null : filename.slice(lastDotIndex + 1);
};

// Core loaders for JS and CSS, each maintaining its own cache
const resource = {
    js: {
        cache: {},
        load: (src, callbackName, options) =>
            new Promise((resolve, reject) => {
                const script = document.createElement('script');

                if (callbackName) {
                    // Assign a global callback if provided
                    window[callbackName] = () => {
                        resolve();
                        delete window[callbackName];
                    };
                } else {
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
                }

                if (options?.async) {
                    script.async = true;
                }

                script.src = src;
                document.head.appendChild(script);
            }),
    },

    css: {
        cache: {},
        load: (href) =>
            new Promise((resolve, reject) => {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.type = 'text/css';
                link.href = href;
                link.onload = () => resolve();
                link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
                document.head.appendChild(link);
            }),
    },
};

// Internal helper to load a single resource (JS or CSS)
// Returns a Promise that resolves once the resource is loaded (or immediately if cached)
const loadResource = (url, explicitType, callbackName, options) => {
    const fileType = explicitType || getFileType(url);
    const loaderEntry = resource[fileType];

    if (!loaderEntry) {
        console.warn(`Unsupported resource type: ${fileType}`);
        return Promise.reject(new Error(`Unsupported resource type: ${fileType}`));
    }

    if (loaderEntry.cache[url]) {
        // Already loaded; resolve immediately
        return Promise.resolve();
    }

    loaderEntry.cache[url] = true;
    return loaderEntry.load(url, callbackName, options);
};

/**
 * Preload a batch of JS/CSS files.
 *
 * @param {Object} params
 * @param {string[]} params.files       - Array of URLs to load.
 * @param {'js'|'css'} [params.type]    - Force a specific type ('js' or 'css'); otherwise inferred from extension.
 * @param {string} [params.callback]    - Global callback name to invoke when a script finishes loading.
 * @param {Object} [params.options]     - Options to pass to the loader (e.g., { async: true } for scripts).
 * @returns {Promise<void[]>}           - Resolves when all resources are either loaded or already cached.
 */
export function preload({ files = [], type, callback, options } = {}) {
    if (!Array.isArray(files) || files.length === 0) {
        return Promise.resolve([]);
    }

    const loadPromises = files.map((url) => loadResource(url, type, callback, options));

    return Promise.all(loadPromises);
}

/**
 * Retrieve a list of already-preloaded files from the cache.
 *
 * @param {'js'|'css'} [key]   - If 'js', returns only JS URLs; if 'css', returns only CSS URLs; otherwise returns both.
 * @returns {string[]}         - Array of URLs that have been loaded and cached.
 */
export function getPreloadFiles(key) {
    switch (key) {
        case 'js':
            return Object.keys(resource.js.cache);
        case 'css':
            return Object.keys(resource.css.cache);
        default:
            return [...Object.keys(resource.js.cache), ...Object.keys(resource.css.cache)];
    }
}
